import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";
import { buildCases, readSheetRows, toExcelBlob } from "./lotUpload";
import { checkLotExcel } from "./lotExcelChecks";

// Every case must have what the later stages print, up to the award: the server reads it from the bank's and
// product's Word files (POST /api/CheckLot) and answers with problems in the same { row, column, problem, fix } form
const checkAgainstDocuments = async (rows, config) => {
  const { cases } = buildCases(rows, config, config.Client_id, config.Product_id);
  try {
    const res = await fetch(`${API_BASE_URL}/api/CheckLot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        Client_id: config.Client_id,
        Product_id: config.Product_id,
        cases: cases.map(({ rowNo, record }) => ({ rowNo, record })),
      }),
    });
    if (!res.ok) throw new Error(await readErrorMessage(res));
    return (await res.json()).problems;
  } catch (e) {
    return [{ row: 1, column: "", problem: `The Excel couldn't be checked against the bank's documents (${e.message}).`, fix: "Choose the file again. If it keeps happening, tell the support team." }];
  }
};

// Reads the LOT Excel and checks it against the product's column setup (Banks & Products), then against what the
// bank's documents will print. Problems are reported in plain words with how to fix them; the LOT template shows the expected format.
const LotExcelUpload = ({
  config,
  onFileChange,
  onErrorFileGenerated,
  onErrorCount,
  setIsDataPresent,
  setValidationErrors,
  onColumnNotes,
}) => {
  const [checking, setChecking] = useState(false);
  const reportErrors = (problems) => {
    setValidationErrors(problems);
    onErrorCount(problems.length);
    onErrorFileGenerated(
      toExcelBlob(
        XLSX,
        problems.map(({ row, column, problem, fix }) => [row, column || "-", problem, fix]),
        ["Row", "Column", "Problem", "How to fix"],
      ),
    );
  };

  // The template the bank fills in: the product's columns, required ones in red, kept exactly as typed
  const handleTemplateDownload = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/${config.Product_id}/lot-template`);
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const link = document.createElement("a");
      link.href = URL.createObjectURL(await res.blob());
      link.download = `LOT template - ${config.Product_name}.xlsx`.replace(/[\\/:*?"<>|]/g, "-");
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      reportErrors([{ row: 1, column: "", problem: e.message, fix: "Fix it in Banks & Products, then download the template again." }]);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = ""; // choosing the same (corrected) file again re-checks it
    if (!file) return;
    const reader = new FileReader();

    reader.onload = async (event) => {
      const workbook = XLSX.read(new Uint8Array(event.target.result), { type: "array", cellDates: true });
      const rows = readSheetRows(XLSX, workbook.Sheets[workbook.SheetNames[0]], config.dateFormat);
      setIsDataPresent(rows.length > 0);

      const { problems, unused, missingOptional } = checkLotExcel(rows, config);
      onColumnNotes?.({ unused, missingOptional });
      if (problems.length) {
        reportErrors(problems);
        return;
      }

      setChecking(true);
      const missing = await checkAgainstDocuments(rows, config);
      setChecking(false);
      if (missing.length) {
        reportErrors(missing);
        return;
      }

      setValidationErrors([]);
      onErrorCount(0);
      onErrorFileGenerated(null);
      onFileChange(rows);
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <Form.Group>
      <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFileUpload} disabled={checking} style={{ fontSize: "12px" }} />
      {checking && <div className="text-muted mt-1" style={{ fontSize: "12px" }}>Checking every case against the bank's documents…</div>}
      <button type="button" className="btn btn-link p-0 mt-1" style={{ fontSize: "12px" }} onClick={handleTemplateDownload}>
        ⬇ Download the LOT template for {config.Product_name} (give this to the bank to fill)
      </button>
    </Form.Group>
  );
};

export default LotExcelUpload;
